/* eslint-disable */
// COPIA del motor de simulación del backend (backend/utils/seasonEngine.js) para correr la
// simulación en el navegador. Si cambia el del backend, re-copiar. Ver docs/ARQUITECTURA_CARRERA.md
// Lógica de cierre y arranque de temporada para el modo carrera.

import { generateCalendar, generateLeaguePhase } from './calendar';import { PARTICIPACION } from './matchEngine';import { worldStandings } from './worldSim';
// Devuelve la tabla de una liga: si la partida es determinística (opts.seed) y la liga NO es la
// del jugador, se recalcula con worldSim (no estaba guardada); si no, se usa la tabla guardada
// (la liga del jugador, o cualquier liga en partidas legacy). `opts` = { seed, temporada,
// esLigaJugador(league) }. Sin opts.seed → siempre tabla guardada (comportamiento legacy).
function leagueTabla(league, opts) {
    if (opts && opts.seed && !(opts.esLigaJugador && opts.esLigaJugador(league))) {
        return worldStandings(league.clubs, opts.seed, opts.temporada, league.nombre);
    }
    const playedAny = league.clubs.some(c => c.pj > 0);
    return playedAny
        ? sortStandings(league.clubs)
        : [...league.clubs].sort((a, b) => (b.estrellas - a.estrellas) || String(a.tm_id).localeCompare(String(b.tm_id)));
}

const EURO_ROUNDS = 8;

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

const ATAQUE_POSITIONS = new Set(['Delantero', 'Extremo', 'Mediocampista']);

// Potencial de desarrollo según edad: cuánto PUEDE crecer (o decaer) en una temporada,
// si juega. Los jóvenes pegan saltos grandes; a partir de los ~30 empieza el declive.
function developmentPotential(edad) {
    if (edad <= 18) return 5.5;
    if (edad <= 20) return 4.5;
    if (edad <= 22) return 3.5;
    if (edad <= 24) return 2.5;
    if (edad <= 27) return 1.3;
    if (edad <= 30) return 0.3;
    if (edad <= 32) return -1.3;
    if (edad <= 34) return -2.5;
    return -4;
}

// Calcula cuánto sube o baja la valoración del jugador al cierre de temporada.
// Pesa sobre todo el RODAJE (minutos + continuidad), mezclado con el rendimiento del
// equipo y, para puestos ofensivos, los goles/asistencias por encima de lo esperado.
function computeRatingDelta({ posicion, edad, rating, stats_temporada, ownClub }) {
    const { partidos, goles, asistencias, minutos_totales } = stats_temporada;

    // Rodaje real: minutos por partido × continuidad (cuántos partidos jugó en la temporada).
    const minutosPromedio = partidos > 0 ? minutos_totales / partidos : 0;
    const factorMinutos = clamp(minutosPromedio / 90, 0, 1);
    const factorContinuidad = clamp(partidos / 32, 0, 1);
    const rodaje = factorMinutos * (0.4 + 0.6 * factorContinuidad); // 0..1

    if (partidos === 0) {
        // Temporada en blanco: los jóvenes se estancan, los grandes se oxidan.
        return edad <= 21 ? -1 : edad <= 30 ? -2 : -3;
    }

    // 1) Desarrollo natural por edad, REALIZADO según el rodaje.
    const potencial = developmentPotential(edad);
    const desarrollo = potencial >= 0
        ? potencial * (0.2 + 0.8 * rodaje)          // sin minutos, casi no crece
        : potencial * (0.5 + 0.5 * (1 - rodaje));   // sin minutos, cae más rápido

    // 2) Cómo le fue al equipo en la liga (compartís el mérito si jugaste).
    const ptsPercent = ownClub.pj > 0 ? ownClub.pts / (ownClub.pj * 3) : 0.45;
    const teamFactor = clamp((ptsPercent - 0.45) * 4, -1.2, 2.2);
    const bonusEquipo = teamFactor * (0.35 + 0.65 * rodaje);

    // 3) Rendimiento individual ofensivo (delanteros, extremos, mediocampistas):
    //    goles + asistencias por 90' por encima de lo esperado para el puesto.
    let bonusIndividual = 0;
    if (ATAQUE_POSITIONS.has(posicion) && minutos_totales > 0) {
        const contribuciones90 = (goles + asistencias) / (minutos_totales / 90);
        const probs = PARTICIPACION[posicion] || PARTICIPACION['Mediocampista'];
        const esperado90 = probs.gol + probs.asistencia;
        bonusIndividual = clamp((contribuciones90 - esperado90) * 5, -1.5, 3.5);
    }

    let raw = desarrollo + bonusEquipo + bonusIndividual;

    // Techo duro progresivo: por encima de 78 cada punto cuesta MUCHO más, y a 99 el
    // crecimiento se anula del todo. La caída es cuadrática, así que pasar de 90 exige
    // varias temporadas seguidas de sobrerrendimiento (rendimiento individual + del equipo,
    // que dependen de las tácticas). Llegar al 99 queda reservado para carreras excepcionales.
    if (raw > 0 && rating > 78) {
        const head = clamp(1 - (rating - 78) / 21, 0, 1); // 1 en 78 → 0 en 99
        raw *= head * head;
    }

    // Declive obligatorio por edad: a partir de los 33 la media baja SÍ O SÍ. Una gran
    // temporada (goles + buen rendimiento del equipo) puede amortiguar la caída, pero nunca
    // revertirla: el resultado queda capado por un techo negativo que se hace más profundo
    // con cada año. Así un veterano se va apagando de forma realista en lugar de seguir
    // subiendo de media mientras rinde.
    if (edad >= 33) {
        const techoDeclive = -1 - (edad - 33) * 0.6; // 33→-1, 35→-2.2, 37→-3.4, 39→-4.6
        raw = Math.min(raw, techoDeclive);
    }

    return Math.round(clamp(raw, -6, 8));
}

function sortStandings(clubs) {
    return [...clubs].sort((a, b) => {
        if (b.pts !== a.pts) return b.pts - a.pts;
        const dgA = a.gf - a.gc;
        const dgB = b.gf - b.gc;
        if (dgB !== dgA) return dgB - dgA;
        return b.gf - a.gf;
    });
}

// Intercambia clubes entre las ligas D1/D2 de un mismo país según ascensos/descensos.
function applyPromotionRelegation(leagues, clubActual, opts) {
    let ascendio = false;
    let descendio = false;

    for (const d1 of leagues) {
        if (d1.division !== 1 || !(d1.descensos > 0)) continue;
        const d2 = leagues.find(l => l.pais === d1.pais && l.division === 2 && l.ascensos > 0);
        if (!d2) continue;

        // En modo determinístico, las ligas ajenas no tienen tabla guardada: se recalcula. La
        // liga del jugador (sea D1 o D2) sí usa su tabla real (la jugó de verdad).
        const tablaD1 = leagueTabla(d1, opts);
        const tablaD2 = leagueTabla(d2, opts);

        const descendidos = tablaD1.slice(tablaD1.length - d1.descensos);
        const ascendidos = tablaD2.slice(0, d2.ascensos);

        const descendidosIds = new Set(descendidos.map(c => c.tm_id));
        const ascendidosIds = new Set(ascendidos.map(c => c.tm_id));

        // Al mover clubes, excluir los que ya existen en la división destino (la data master
        // puede tener el mismo club en ambas divisiones y duplicaría tm_ids en la liga).
        const d1Restantes = d1.clubs.filter(c => !descendidosIds.has(c.tm_id));
        const d2Restantes = d2.clubs.filter(c => !ascendidosIds.has(c.tm_id));
        const d1Ids = new Set(d1Restantes.map(c => c.tm_id));
        const d2Ids = new Set(d2Restantes.map(c => c.tm_id));

        d1.clubs = d1Restantes.concat(ascendidos.filter(c => !d1Ids.has(c.tm_id)));
        d2.clubs = d2Restantes.concat(descendidos.filter(c => !d2Ids.has(c.tm_id)));

        if (clubActual && descendidosIds.has(clubActual.tm_id)) {
            clubActual.division = 2;
            descendio = true;
        } else if (clubActual && ascendidosIds.has(clubActual.tm_id)) {
            clubActual.division = 1;
            ascendio = true;
        }
    }

    return { ascendio, descendio };
}

// Resetea las tablas de cada liga para la nueva temporada (sin generar calendarios: de eso se
// encarga setupSeasonCalendars en el controller, que solo le da calendario a la liga del jugador
// y simula el resto a tabla final, para mantener el documento chico).
function startNewSeason(leagues) {
    for (const league of leagues) {
        // Auto-reparación: si quedaron tm_ids duplicados de temporadas anteriores, descartarlos.
        const seen = new Set();
        league.clubs = league.clubs.filter(c => {
            if (!c.tm_id || seen.has(c.tm_id)) return false;
            seen.add(c.tm_id);
            return true;
        });
        for (const club of league.clubs) {
            club.pj = 0;
            club.pg = 0;
            club.pe = 0;
            club.pp = 0;
            club.gf = 0;
            club.gc = 0;
            club.pts = 0;
        }
        league.calendario = [];
    }
}

function toCompetitionClub(club, league) {
    return {
        tm_id: club.tm_id,
        nombre: club.nombre,
        logo: club.logo,
        pais: league.pais,
        estrellas: club.estrellas,
        pj: 0, pg: 0, pe: 0, pp: 0, gf: 0, gc: 0, pts: 0,
    };
}

// Selecciona los clubes de Champions y Europa League para la próxima temporada según
// los cupos de cada liga de primera división. Si la liga ya jugó (algún club con pj > 0)
// usa la tabla final; si no (temporada 1), ordena por estrellas.
function selectEuropeanPools(leagues, opts) {
    const champions = [];
    const europa = [];

    for (const league of leagues) {
        if (league.division !== 1) continue;
        if (!league.cupos_champions && !league.cupos_europa) continue;

        const ordered = leagueTabla(league, opts);

        const clClubs = ordered.slice(0, league.cupos_champions);
        const elClubs = ordered.slice(league.cupos_champions, league.cupos_champions + league.cupos_europa);

        for (const c of clClubs) champions.push(toCompetitionClub(c, league));
        for (const c of elClubs) europa.push(toCompetitionClub(c, league));
    }

    return { champions, europa };
}

// Arma una competición europea: tabla de clubes clasificados + fase de liga de 8 jornadas.
function buildEuropeanCompetition(clubs) {
    return {
        clubs,
        calendario: generateLeaguePhase(clubs.map(c => c.tm_id), EURO_ROUNDS),
        fase: 'grupos',
        ko_ronda: '',
        ko_seeds: [],
        ko_vivos: [],
        ko_ida: [],
        ko_resultados: [],
        campeon_tm_id: '',
    };
}

// Jornadas de la liga del jugador en las que se disputan partidos europeos,
// repartidas a lo largo de la temporada.
function computeEuroJornadas(totalJornadas) {
    const slot = Math.max(1, Math.floor(totalJornadas / (EURO_ROUNDS + 1)));
    return Array.from({ length: EURO_ROUNDS }, (_, i) => slot * (i + 1));
}

// ===== Eliminatorias (knockout) de las copas europeas =====
// Formato UEFA 2024: 36 equipos, fase de liga de 8 jornadas, y después:
//   1-8  → octavos directo;  9-24 → playoff de 16avos;  25-36 → eliminados.
const KO_RONDAS = ['16avos', 'octavos', 'cuartos', 'semis', 'final'];

// Secuencia de slots de eliminatoria: cada ronda salvo la final se juega a ida y vuelta.
// 4 rondas × 2 + final = 9 jornadas europeas en la 2ª mitad de la temporada.
const KO_LEGS = [
    { ronda: '16avos', leg: 'ida' }, { ronda: '16avos', leg: 'vuelta' },
    { ronda: 'octavos', leg: 'ida' }, { ronda: 'octavos', leg: 'vuelta' },
    { ronda: 'cuartos', leg: 'ida' }, { ronda: 'cuartos', leg: 'vuelta' },
    { ronda: 'semis', leg: 'ida' }, { ronda: 'semis', leg: 'vuelta' },
    { ronda: 'final', leg: 'unico' },
];

// Devuelve n jornadas DISTINTAS repartidas uniformemente dentro de [lo, hi].
function spreadJornadas(n, lo, hi) {
    const out = [];
    const span = hi - lo;
    for (let i = 0; i < n; i++) {
        let j = lo + Math.round((span * (i + 1)) / (n + 1));
        if (out.length && j <= out[out.length - 1]) j = out[out.length - 1] + 1;
        out.push(Math.min(hi, j));
    }
    for (let i = out.length - 1; i > 0; i--) {
        if (out[i] <= out[i - 1]) out[i - 1] = out[i] - 1;
    }
    return out;
}

// Mapa jornada_de_liga → { tipo: 'grupo'|'ko', ronda, leg } para toda la temporada del jugador.
// Las 8 jornadas de fase de liga van en la 1ª mitad; las 9 eliminatorias (ida/vuelta) en la 2ª.
function computeEuroSchedule(totalJornadas) {
    const mid = Math.floor(totalJornadas / 2);
    const map = new Map();
    const grupos = spreadJornadas(EURO_ROUNDS, 1, Math.max(EURO_ROUNDS + 1, mid));
    grupos.forEach((j, i) => map.set(j, { tipo: 'grupo', ronda: i + 1 }));
    const ko = spreadJornadas(KO_LEGS.length, Math.max(...grupos) + 1, totalJornadas);
    ko.forEach((j, i) => { if (!map.has(j)) map.set(j, { tipo: 'ko', ...KO_LEGS[i] }); });
    return map;
}

// Arranca la fase eliminatoria de una competición a partir de la tabla de la fase de liga.
function startKnockout(comp) {
    const tabla = sortStandings(comp.clubs);
    comp.ko_seeds = tabla.slice(0, 8).map(c => c.tm_id);             // esperan en octavos
    const playoff = tabla.slice(8, 24).map(c => c.tm_id);           // 16 equipos al playoff
    // Emparejar 9º-24º, 10º-23º, ... → pares consecutivos = llaves.
    const vivos = [];
    for (let i = 0; i < playoff.length / 2; i++) {
        vivos.push(playoff[i]);
        vivos.push(playoff[playoff.length - 1 - i]);
    }
    comp.ko_vivos = vivos;
    comp.ko_ronda = '16avos';
    comp.fase = 'knockout';
    comp.ko_resultados = [];
    comp.campeon_tm_id = '';
}

// Pares consecutivos de la lista de vivos (el último queda con BYE si la lista es impar).
function pairsFromVivos(vivos) {
    const pairs = [];
    for (let i = 0; i < vivos.length; i += 2) pairs.push([vivos[i], vivos[i + 1] || null]);
    return pairs;
}

// Avanza la competición a la ronda siguiente con los ganadores (en orden de llave).
function advanceKnockout(comp, winners) {
    if (comp.ko_ronda === 'final') {
        comp.campeon_tm_id = winners[0] || '';
        comp.fase = 'finalizado';
        comp.ko_ronda = '';
        comp.ko_vivos = [];
        return;
    }
    if (comp.ko_ronda === '16avos') {
        // Inyectar los 8 cabezas de serie: cada uno contra un ganador del playoff.
        const next = [];
        for (let i = 0; i < comp.ko_seeds.length; i++) {
            next.push(comp.ko_seeds[i]);
            if (winners[i] !== undefined) next.push(winners[i]);
        }
        comp.ko_vivos = next;
        comp.ko_ronda = 'octavos';
        return;
    }
    const idx = KO_RONDAS.indexOf(comp.ko_ronda);
    comp.ko_vivos = winners;
    comp.ko_ronda = KO_RONDAS[idx + 1];
}

// ===== Copas sudamericanas (CONMEBOL): Libertadores y Sudamericana =====
// Formato "viejo": 8 grupos de 4 equipos. Fase de grupos a ida y vuelta (6 jornadas).
// Libertadores: 1º y 2º de cada grupo → octavos (16). El 3º cae a 16avos de Sudamericana.
// Sudamericana: 1º de cada grupo → octavos (8). El 2º juega 16avos contra un 3º de Libertadores.
const CONMEBOL_GROUPS = 8;

// Selecciona los clubes de Libertadores y Sudamericana para la próxima temporada según los
// cupos de cada liga sudamericana de primera división (cupos_libertadores / cupos_sudamericana).
function selectConmebolPools(leagues, opts) {
    const libertadores = [];
    const sudamericana = [];

    for (const league of leagues) {
        if (league.division !== 1) continue;
        if (!league.cupos_libertadores && !league.cupos_sudamericana) continue;

        const ordered = leagueTabla(league, opts);

        const libCupos = league.cupos_libertadores || 0;
        const sudCupos = league.cupos_sudamericana || 0;
        const libClubs = ordered.slice(0, libCupos);
        const sudClubs = ordered.slice(libCupos, libCupos + sudCupos);

        for (const c of libClubs) libertadores.push(toCompetitionClub(c, league));
        for (const c of sudClubs) sudamericana.push(toCompetitionClub(c, league));
    }

    return { libertadores, sudamericana };
}

// Máximo de clubes de un mismo país por grupo: 2 para Argentina y Brasil, 1 para el resto.
function countryGroupLimit(pais) {
    return (pais === 'Argentina' || pais === 'Brasil') ? 2 : 1;
}

// Reparte los clubes en 8 grupos por "bombos" según estrellas: el mejor de cada bombo cae en
// un grupo distinto, de modo que cada grupo quede equilibrado (un cabeza de serie, etc.).
// Además respeta el límite de clubes por país en cada grupo (countryGroupLimit), recurriendo
// a un grupo alternativo con cupo libre cuando el grupo "preferido" del bombo no lo permite.
function assignGroups(clubs) {
    const sorted = [...clubs].sort((a, b) =>
        (b.estrellas - a.estrellas) || String(a.tm_id).localeCompare(String(b.tm_id)));

    // Baraja el orden DENTRO de cada bombo (de a CONMEBOL_GROUPS) para que los grupos no salgan
    // siempre con la misma composición temporada tras temporada, manteniendo el equilibrio de
    // fuerzas (los cabezas de serie siguen repartidos un bombo por grupo).
    const ordered = [];
    for (let i = 0; i < sorted.length; i += CONMEBOL_GROUPS) {
        const pote = sorted.slice(i, i + CONMEBOL_GROUPS);
        for (let j = pote.length - 1; j > 0; j--) {
            const k = Math.floor(Math.random() * (j + 1));
            [pote[j], pote[k]] = [pote[k], pote[j]];
        }
        ordered.push(...pote);
    }

    const groupSize = Array(CONMEBOL_GROUPS).fill(0);
    const groupCountry = Array.from({ length: CONMEBOL_GROUPS }, () => new Map());

    ordered.forEach((c, i) => {
        const pot = Math.floor(i / CONMEBOL_GROUPS);
        // Rotación por bombo: dentro de cada bombo de 8, el grupo "preferido" es (posición + bombo) % 8.
        const preferred = ((i % CONMEBOL_GROUPS) + pot) % CONMEBOL_GROUPS;
        const limit = countryGroupLimit(c.pais);
        const fits = (g) => groupSize[g] < 4 && (groupCountry[g].get(c.pais) || 0) < limit;

        let chosen = fits(preferred) ? preferred : -1;
        if (chosen === -1) {
            // Grupo con menos equipos que respete el límite de país.
            for (let g = 0; g < CONMEBOL_GROUPS; g++) {
                if (!fits(g)) continue;
                if (chosen === -1 || groupSize[g] < groupSize[chosen]) chosen = g;
            }
        }
        if (chosen === -1) {
            // Fallback: ignora el límite de país, solo respeta el cupo de 4 por grupo.
            for (let g = 0; g < CONMEBOL_GROUPS; g++) {
                if (groupSize[g] >= 4) continue;
                if (chosen === -1 || groupSize[g] < groupSize[chosen]) chosen = g;
            }
        }

        c.grupo = chosen;
        groupSize[chosen]++;
        groupCountry[chosen].set(c.pais, (groupCountry[chosen].get(c.pais) || 0) + 1);
    });

    return ordered;
}

// Calendario de fase de grupos: doble round-robin DENTRO de cada grupo, fusionando todos los
// grupos por jornada (jornada k = los partidos de la ronda k de los 8 grupos).
function buildGroupCalendar(clubs) {
    const byGroup = new Map();
    for (const c of clubs) {
        if (!byGroup.has(c.grupo)) byGroup.set(c.grupo, []);
        byGroup.get(c.grupo).push(c.tm_id);
    }
    const perGroup = [...byGroup.values()].map(ids => generateCalendar(ids));
    const maxJ = perGroup.reduce((m, g) => Math.max(m, g.length), 0);

    const cal = [];
    for (let j = 0; j < maxJ; j++) {
        const partidos = [];
        for (const g of perGroup) if (g[j]) partidos.push(...g[j].partidos);
        cal.push({ jornada: j + 1, partidos });
    }
    return cal;
}

// Arma una copa sudamericana: clubes repartidos en 8 grupos + fase de grupos a ida y vuelta.
function buildConmebolCompetition(clubs) {
    const withGroups = assignGroups(clubs);
    return {
        clubs: withGroups,
        calendario: buildGroupCalendar(withGroups),
        fase: 'grupos',
        ko_ronda: '',
        ko_seeds: [],
        ko_vivos: [],
        ko_ida: [],
        ko_resultados: [],
        campeon_tm_id: '',
    };
}

// Tablas finales por grupo: { grupoIdx: [clubs ordenados] }. Solo considera clubes con grupo >= 0.
function groupStandings(comp) {
    const byGroup = new Map();
    for (const c of comp.clubs) {
        if (c.grupo == null || c.grupo < 0) continue;
        if (!byGroup.has(c.grupo)) byGroup.set(c.grupo, []);
        byGroup.get(c.grupo).push(c);
    }
    const out = {};
    for (const [g, list] of byGroup) out[g] = sortStandings(list);
    return out;
}

// Arranca las eliminatorias de AMBAS copas a la vez (están acopladas: los 3º de Libertadores
// bajan a los 16avos de Sudamericana). Debe llamarse una sola vez, al cerrar la fase de grupos.
function startConmebolKnockouts(lib, sudam) {
    const libStand = groupStandings(lib);
    const sudStand = groupStandings(sudam);
    const groups = Object.keys(libStand).map(Number).sort((a, b) => a - b);
    const sudGroups = Object.keys(sudStand).map(Number).sort((a, b) => a - b);

    // --- Libertadores: 1º y 2º → octavos; 3º → baja a Sudamericana ---
    const libPrimeros = [];
    const libSegundos = [];
    const libTerceros = [];
    for (const g of groups) {
        const tabla = libStand[g] || [];
        if (tabla[0]) libPrimeros.push(tabla[0].tm_id);
        if (tabla[1]) libSegundos.push(tabla[1].tm_id);
        if (tabla[2]) libTerceros.push(tabla[2].tm_id);
    }
    // Octavos: cada 1º contra un 2º de otro grupo (offset +1 para evitar revanchas del grupo).
    const libVivos = [];
    for (let i = 0; i < libPrimeros.length; i++) {
        libVivos.push(libPrimeros[i]);
        const seg = libSegundos[(i + 1) % libSegundos.length];
        if (seg !== undefined) libVivos.push(seg);
    }
    lib.ko_vivos = libVivos;
    lib.ko_ronda = 'octavos';
    lib.fase = 'knockout';
    lib.ko_seeds = [];
    lib.ko_ida = [];
    lib.ko_resultados = [];
    lib.campeon_tm_id = '';

    // --- Sudamericana: 1º → octavos (cabezas de serie); 2º → 16avos vs 3º de Libertadores ---
    // Los 3º de Libertadores deben existir como clubes en Sudamericana para poder simular/mostrar.
    const sudByTmId = new Map(sudam.clubs.map(c => [c.tm_id, c]));
    const libByTmId = new Map(lib.clubs.map(c => [c.tm_id, c]));
    for (const terceroId of libTerceros) {
        if (sudByTmId.has(terceroId)) continue;
        const src = libByTmId.get(terceroId);
        if (!src) continue;
        sudam.clubs.push({
            tm_id: src.tm_id, nombre: src.nombre, logo: src.logo, pais: src.pais,
            estrellas: src.estrellas, grupo: -1,
            pj: 0, pg: 0, pe: 0, pp: 0, gf: 0, gc: 0, pts: 0,
        });
    }

    const sudPrimeros = [];
    const sudSegundos = [];
    for (const g of sudGroups) {
        const tabla = sudStand[g] || [];
        if (tabla[0]) sudPrimeros.push(tabla[0].tm_id);
        if (tabla[1]) sudSegundos.push(tabla[1].tm_id);
    }
    // 16avos: 2º de Sudamericana vs 3º de Libertadores (pares consecutivos = llaves).
    const sudVivos = [];
    const n16 = Math.min(sudSegundos.length, libTerceros.length);
    for (let i = 0; i < n16; i++) {
        sudVivos.push(sudSegundos[i]);
        sudVivos.push(libTerceros[i]);
    }
    // Cualquier 2º sin rival (si faltan terceros) pasa con BYE (par impar lo resuelve pairsFromVivos).
    for (let i = n16; i < sudSegundos.length; i++) sudVivos.push(sudSegundos[i]);

    sudam.ko_seeds = sudPrimeros;   // esperan en octavos
    sudam.ko_vivos = sudVivos;
    sudam.ko_ronda = '16avos';
    sudam.fase = 'knockout';
    sudam.ko_ida = [];
    sudam.ko_resultados = [];
    sudam.campeon_tm_id = '';
}

export {    computeRatingDelta,
    applyPromotionRelegation,
    startNewSeason,
    sortStandings,
    selectEuropeanPools,
    buildEuropeanCompetition,
    selectConmebolPools,
    buildConmebolCompetition,
    startConmebolKnockouts,
    groupStandings,
    computeEuroJornadas,
    computeEuroSchedule,
    startKnockout,
    pairsFromVivos,
    advanceKnockout,
    KO_RONDAS,
    KO_LEGS,
    EURO_ROUNDS,
    CONMEBOL_GROUPS,
};
